import { JSX } from 'react';
import { enterpriseRequestStatus } from "../../../defaults/defaults";
import {
    List,
    TextField,
    Datagrid,
    DateField,
    EditButton,
    SelectField,
    useGetList,
    ReferenceField
} from 'react-admin';
import {
    Box,
} from '@mui/material';
import { DashboardCard } from "./DashboardCard";
import { NewItemsBadge, ViewAllLink, dashboardColumnWidths, dashboardListSx } from "./ListWidgetChrome";
// The pulse dot's rules live here. This widget used to render correctly only because a *sibling*
// widget happened to import the same stylesheet first.
import './styles/token-history-graphs.scss';

// The <List> below and the count query that drives the badge must issue the *same*
// react-query key, otherwise the widget fires two requests for the same rows. Declaring the
// params once and feeding both call sites is what keeps them identical.
const LIST_QUERY = {
    pagination: { page: 1, perPage: 10 },
    sort: { field: 'id', order: 'DESC' as const },
    filter: { status: 'new' },
};

export const EnterpriseRequestsData: () => JSX.Element = (): JSX.Element => {

    const { total, isPending, } = useGetList('enterprise_requests', LIST_QUERY);

    const ready: boolean = !isPending && total !== undefined;

    return (
        <DashboardCard
            eyebrow="Needs attention"
            title="Enterprise requests"
            actions={ ready ? <NewItemsBadge total={ total } /> : undefined }
        >
            { /* Id, Name, Email, Status, Account, Date added, Edit. The email is the longest value
                 in the table and the status is one short word, so they must not share a width. */ }
            <Box
                className="enterprise-requests"
                sx={ {
                    ...dashboardListSx,
                    ...dashboardColumnWidths(['56px', '20%', '30%', '16%', '13%', '21%', '64px']),
                } }
            >
                <List
                    // Not just "no title": omitting this makes <List> push its resource name into
                    // the app bar's title portal, where it stacks after the dashboard's own title.
                    title={ false }
                    resource="enterprise_requests"
                    actions={false}
                    // A dashboard card is a summary, so the rows-per-page footer was chrome for a
                    // control nobody would use here. "View all" below goes to the full list.
                    pagination={ false }
                    perPage={ LIST_QUERY.pagination.perPage }
                    sort={ LIST_QUERY.sort }
                    filter={ LIST_QUERY.filter }
                    empty={ false }
                >
                    <Datagrid bulkActionButtons={false} >
                        <TextField source="id" />
                        <TextField source="name"/>
                        <TextField source="email" />
                        <SelectField source="status" choices={ enterpriseRequestStatus } />
                        { /* "Customer Request" cost more width than the column's one-word content
                             justified, and it links to a customer account, so "Account" is both
                             shorter and more accurate. */ }
                        <ReferenceField source="customerAccount" reference="customer_accounts" label="Account">
                            <span>{ "View" }</span>
                        </ReferenceField>
                        { /* Date only, no `showTime`: the full list a click away carries the time,
                             and a timestamp is twice the width in a half-width card. */ }
                        <DateField source="createdAt" label="Date added" />
                        { /* Icon only: the word "Edit" costs a column's worth of width in a
                             half-width card, and the pencil is already the app-wide convention. */ }
                        <EditButton label="" />
                    </Datagrid>
                </List>
            </Box>
            <ViewAllLink to="/enterprise_requests" label="View all enterprise requests" />
        </DashboardCard>
    );

}
